import { useState } from "react";

interface ComplianceCheck {
  rule: string;
  description: string;
  status: "pass" | "fail" | "warning";
  details?: string;
}

interface CompliancePanelProps {
  checks: ComplianceCheck[];
  region: string;
  coaFigure: string;
}

function StatusIcon({ status }: { status: ComplianceCheck["status"] }) {
  if (status === "pass") {
    return (
      <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
        <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      </div>
    );
  }
  if (status === "fail") {
    return (
      <div className="w-5 h-5 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
        <svg className="w-3 h-3 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </div>
    );
  }
  // warning
  return (
    <div className="w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
      <svg className="w-3 h-3 text-amber-600 dark:text-amber-400" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    </div>
  );
}

const STATUS_ROW_BG: Record<ComplianceCheck["status"], string> = {
  pass: "hover:bg-green-50/50 dark:hover:bg-green-900/5",
  fail: "hover:bg-red-50/50 dark:hover:bg-red-900/5",
  warning: "hover:bg-amber-50/50 dark:hover:bg-amber-900/5",
};

export default function CompliancePanel({ checks, region, coaFigure }: CompliancePanelProps) {
  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());

  const passCount = checks.filter((c) => c.status === "pass").length;
  const failCount = checks.filter((c) => c.status === "fail").length;
  const warnCount = checks.filter((c) => c.status === "warning").length;
  const total = checks.length;

  const toggleExpand = (rule: string) => {
    setExpandedRules((prev) => {
      const next = new Set(prev);
      if (next.has(rule)) {
        next.delete(rule);
      } else {
        next.add(rule);
      }
      return next;
    });
  };

  const overallStatus: ComplianceCheck["status"] =
    failCount > 0 ? "fail" : warnCount > 0 ? "warning" : "pass";

  const summaryBg = {
    pass: "bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800",
    fail: "bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800",
    warning: "bg-amber-50 border-amber-200 dark:bg-amber-900/10 dark:border-amber-800",
  }[overallStatus];

  const summaryText = {
    pass: "text-green-800 dark:text-green-200",
    fail: "text-red-800 dark:text-red-200",
    warning: "text-amber-800 dark:text-amber-200",
  }[overallStatus];

  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-white/[0.05]">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">
            COA Compliance Checks
          </h3>
          <div className="flex items-center gap-2">
            {region && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {region} District
              </span>
            )}
            {coaFigure && (
              <span className="text-xs px-2 py-0.5 rounded bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300 font-medium">
                Figure {coaFigure}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-4 space-y-4">
        {/* Summary banner */}
        {total > 0 && (
          <div className={`rounded-lg border p-3 ${summaryBg}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <StatusIcon status={overallStatus} />
                <p className={`text-sm font-medium ${summaryText}`}>
                  {passCount} of {total} check{total !== 1 ? "s" : ""} passed
                </p>
              </div>
              <div className="flex items-center gap-3 text-xs">
                {passCount > 0 && (
                  <span className="text-green-700 dark:text-green-400 font-medium">
                    {passCount} passed
                  </span>
                )}
                {failCount > 0 && (
                  <span className="text-red-700 dark:text-red-400 font-medium">
                    {failCount} failed
                  </span>
                )}
                {warnCount > 0 && (
                  <span className="text-amber-700 dark:text-amber-400 font-medium">
                    {warnCount} warnings
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Checks list */}
        {checks.length === 0 ? (
          <div className="text-center py-6 text-gray-500 dark:text-gray-400">
            <p className="text-sm">No compliance checks available.</p>
            <p className="text-xs mt-1">Complete the plugging plan to run checks.</p>
          </div>
        ) : (
          <div className="space-y-1">
            {/* Failed first, then warnings, then passed */}
            {[
              ...checks.filter((c) => c.status === "fail"),
              ...checks.filter((c) => c.status === "warning"),
              ...checks.filter((c) => c.status === "pass"),
            ].map((check) => {
              const isExpanded = expandedRules.has(check.rule);
              const hasDetails = !!check.details;

              return (
                <div
                  key={check.rule}
                  className={`rounded-lg border border-gray-100 dark:border-white/[0.05] transition-colors ${STATUS_ROW_BG[check.status]}`}
                >
                  <button
                    onClick={() => hasDetails && toggleExpand(check.rule)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left ${hasDetails ? "cursor-pointer" : "cursor-default"}`}
                  >
                    <StatusIcon status={check.status} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                        {check.description}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {check.rule}
                      </p>
                    </div>
                    {hasDetails && (
                      <svg
                        className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </button>

                  {isExpanded && check.details && (
                    <div className="px-3 pb-3">
                      <div className="ml-8 rounded bg-gray-50 dark:bg-gray-800 p-2 border border-gray-200 dark:border-gray-700">
                        <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                          {check.details}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* NM-specific rule reference */}
        <div className="pt-2 border-t border-gray-200 dark:border-white/[0.05]">
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Checks based on NM OCD COA Figure {coaFigure || "—"} requirements.
            Rules include: surface plug, formation plugs, max spacing ({region === "Hobbs" ? "2,000" : "3,000"} ft),
            CIBP cap (100 ft min), 25 sacks per plug, cement class by depth.
          </p>
        </div>
      </div>
    </div>
  );
}
